import React, { FC, useMemo } from 'react';
import { useIspmContext } from '../IspmContext';
import { Button, ButtonStyles, Dropdown, Icons, Label, ListItemProps } from '@silverfort/sf-components';
import { DEFAULT_LAST_CHANGES_FILTER, LAST_CHANGES_FILTER_DROPDOWN_ITEMS } from '../constants';
import IspmReportsSelect from './Reports/IspmReportsSelect';
import useFeature from '../../../components/SystemSettings/hooks/featureFlags/useFeature';
import { FEATURE_FLAG_SEVERITIES, FEATURE_FLAGS_NAMES } from '@silverfort-nodelibs/common/utils/consts';

interface IspmHeaderProps {
    onCustomizationClick: () => void;
}

const IspmHeader: FC<IspmHeaderProps> = ({ onCustomizationClick }) => {
    const { domainFilter, insightsData, selectedLastChangesFilter, setDomainFilter, setSelectedLastChangesFilter, hasIspmEditorPermission } =
        useIspmContext();

    const oldRiskReportGeneration = useFeature(FEATURE_FLAGS_NAMES.OldRiskReportGeneration);
    const isOldRiskReportGenerationEnabled = oldRiskReportGeneration === FEATURE_FLAG_SEVERITIES.ACTIVE;

    const domainOptions = useMemo(() => {
        if (!insightsData?.domains?.length) return [];

        return insightsData.domains.map<ListItemProps<string>>((domain) => ({
            title: domain,
            value: domain
        }));
    }, [insightsData?.domains]);
    const domains = useMemo<Set<string>>(() => new Set(insightsData?.domains), [insightsData?.domains]);

    const clearSelectedDomains = () => {
        setDomainFilter(null);
    };

    const updateSelectedDomains = (value?: string | Array<string>) => {
        if (!value) return;

        const selectedDomains: Array<string> = [];

        if (Array.isArray(value)) {
            value.forEach((option) => {
                if (domains.has(option)) {
                    selectedDomains.push(option);
                }
            });
        } else {
            if (domains.has(value)) {
                selectedDomains.push(value);
            }
        }

        setDomainFilter(selectedDomains.length ? selectedDomains : null);
    };

    const updateSelectedLastChange = (value?: string | Array<string>) => {
        if (!value) return;

        let lastChange: string;

        if (Array.isArray(value)) {
            lastChange = value.length ? value[0] : DEFAULT_LAST_CHANGES_FILTER;
        } else {
            lastChange = value;
        }

        setSelectedLastChangesFilter(lastChange);
    };

    return (
        <section className="flex justify-between items-center">
            <Dropdown
                items={domainOptions}
                menuClassName="w-[200px]"
                multiple
                onChange={updateSelectedDomains}
                onRemove={clearSelectedDomains}
                removable={Boolean(domainFilter?.length)}
                searchable
                selectedItems={[...(domainFilter ?? [])]}
                title="Domain"
                variant="filter"
            />

            <div className="h-full flex items-center gap-2">
                <Label text="Time Range" />

                <Dropdown
                    className="w-32"
                    items={LAST_CHANGES_FILTER_DROPDOWN_ITEMS}
                    onChange={updateSelectedLastChange}
                    selectedItems={selectedLastChangesFilter}
                    size="small"
                    variant="trigger"
                />

                {isOldRiskReportGenerationEnabled && <IspmReportsSelect />}

                <Button
                    text="Customization"
                    buttonStyle={ButtonStyles.Primary}
                    iconLeft={Icons.Settings}
                    onClick={onCustomizationClick}
                    disabled={!hasIspmEditorPermission}
                />
            </div>
        </section>
    );
};

export default IspmHeader;
